/**
 * Junto Frontend BookingPopup Component
 * 
 * This is the modular booking popup that can be used by any studio.
 * It maintains exact visual equivalence to the Sol Room booking popup while
 * being configurable for other studios through theme props.
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Modal, Button, TabGroup, Tab, TabPanel } from '../ui/index.js';
import { solRoomTheme } from '../../themes/sol-room.js';

const BookingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme?.spacing?.lg || solRoomTheme.spacing.lg};
`;

const CalendarSection = styled.div`
  padding: ${props => props.theme?.spacing?.md || solRoomTheme.spacing.md};
  border: 1px solid ${props => props.theme?.colors?.neutral?.light || solRoomTheme.colors.neutral.light};
  border-radius: 8px;
  background: ${props => props.theme?.colors?.neutral?.lightest || solRoomTheme.colors.neutral.lightest};
`;

const ClassList = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme?.spacing?.md || solRoomTheme.spacing.md};
  max-height: 400px;
  overflow-y: auto;
`;

const ClassCard = styled.div`
  padding: ${props => props.theme?.spacing?.md || solRoomTheme.spacing.md};
  border: 1px solid ${props => props.theme?.colors?.neutral?.light || solRoomTheme.colors.neutral.light};
  border-radius: 8px;
  background: ${props => props.theme?.colors?.primary?.background || solRoomTheme.colors.primary.background};
  transition: ${props => props.theme?.transitions?.default || solRoomTheme.transitions.default};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${props => props.theme?.shadows?.light || solRoomTheme.shadows.light};
  }
`;

const ClassHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${props => props.theme?.spacing?.sm || solRoomTheme.spacing.sm};
`;

const ClassName = styled.h4`
  font-family: ${props => props.theme?.typography?.primary?.fontFamily || solRoomTheme.typography.primary.fontFamily};
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.theme?.colors?.primary?.text || solRoomTheme.colors.primary.text};
  margin: 0;
`;

const ClassTime = styled.div`
  font-family: ${props => props.theme?.typography?.secondary?.fontFamily || solRoomTheme.typography.secondary.fontFamily};
  font-size: 0.9rem;
  color: ${props => props.theme?.colors?.accent?.main || solRoomTheme.colors.accent.main};
  font-weight: 500;
`;

const ClassDetails = styled.div`
  font-family: ${props => props.theme?.typography?.secondary?.fontFamily || solRoomTheme.typography.secondary.fontFamily};
  font-size: 0.85rem;
  color: ${props => props.theme?.colors?.primary?.text || solRoomTheme.colors.primary.text};
  opacity: 0.7;
  line-height: 1.4;
  margin-bottom: ${props => props.theme?.spacing?.md || solRoomTheme.spacing.md};
`;

const ClassFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ClassCredits = styled.div`
  font-family: ${props => props.theme?.typography?.secondary?.fontFamily || solRoomTheme.typography.secondary.fontFamily};
  font-size: 0.85rem;
  color: ${props => props.theme?.colors?.primary?.text || solRoomTheme.colors.primary.text};
  font-weight: 500;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => props.theme?.spacing?.xl || solRoomTheme.spacing.xl};
  color: ${props => props.theme?.colors?.primary?.text || solRoomTheme.colors.primary.text};
  opacity: 0.6;
  font-family: ${props => props.theme?.typography?.secondary?.fontFamily || solRoomTheme.typography.secondary.fontFamily};
`;

const BookingPopup = ({ 
  isOpen, 
  onClose, 
  onBookClass,
  classes = [],
  selectedDate = new Date(),
  onDateChange,
  isLoading = false,
  theme = solRoomTheme 
}) => {
  const [activeTab, setActiveTab] = useState('calendar');

  const handleBookClass = async (classItem) => {
    if (onBookClass) {
      try {
        await onBookClass(classItem);
      } catch (error) {
        console.error('Booking failed:', error);
      }
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (time) => {
    return new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const renderCalendarView = () => (
    <CalendarSection theme={theme}>
      <h3 style={{ 
        fontFamily: theme?.typography?.primary?.fontFamily || solRoomTheme.typography.primary.fontFamily,
        marginBottom: theme?.spacing?.md || solRoomTheme.spacing.md 
      }}>
        Select Date
      </h3>
      {/* Calendar component would go here */}
      <div style={{ 
        padding: theme?.spacing?.lg || solRoomTheme.spacing.lg,
        textAlign: 'center',
        color: theme?.colors?.primary?.text || solRoomTheme.colors.primary.text,
        opacity: 0.7 
      }}>
        Calendar component placeholder
        <br />
        Selected: {formatDate(selectedDate)}
      </div>
    </CalendarSection>
  );

  const renderClassList = () => (
    <AnimatePresence mode="wait">
      <ClassList 
        theme={theme}
        key={selectedDate.toISOString()} // Key by date to trigger animation on date change
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
      >
        {classes.length === 0 ? (
          <EmptyState theme={theme}>
            No classes available for the selected date
          </EmptyState>
        ) : (
          classes.map((classItem) => (
          <ClassCard key={classItem.id} theme={theme}>
            <ClassHeader theme={theme}>
              <ClassName theme={theme}>{classItem.name}</ClassName>
              <ClassTime theme={theme}>
                {formatTime(classItem.startTime)}
              </ClassTime>
            </ClassHeader>
            
            <ClassDetails theme={theme}>
              Instructor: {classItem.instructor}<br />
              Duration: {classItem.duration} minutes<br />
              Location: {classItem.location}
            </ClassDetails>
            
            <ClassFooter theme={theme}>
              <ClassCredits theme={theme}>
                {classItem.credits} credit{classItem.credits !== 1 ? 's' : ''}
              </ClassCredits>
              
              <Button
                variant="filled"
                size="small"
                onClick={() => handleBookClass(classItem)}
                disabled={isLoading || classItem.spotsRemaining === 0}
                theme={theme}
              >
                {classItem.spotsRemaining === 0 ? 'Full' : 'Book'}
              </Button>
            </ClassFooter>
          </ClassCard>
        ))
      )}
      </ClassList>
    </AnimatePresence>
  );

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      maxWidth="600px"
      theme={theme}
    >
      <BookingContainer theme={theme}>
        <TabGroup theme={theme}>
          <Tab 
            active={activeTab === 'calendar'} 
            onClick={() => setActiveTab('calendar')}
            theme={theme}
          >
            Calendar
          </Tab>
          <Tab 
            active={activeTab === 'classes'} 
            onClick={() => setActiveTab('classes')}
            theme={theme}
          >
            Classes ({classes.length})
          </Tab>
        </TabGroup>

        <TabPanel theme={theme}>
          {activeTab === 'calendar' && renderCalendarView()}
          {activeTab === 'classes' && renderClassList()}
        </TabPanel>
      </BookingContainer>
    </Modal>
  );
};

export default BookingPopup;